import { getRoutecampStyleUrl } from '@/services/routecampTilesUrl'

import type { MapOptions } from 'maplibre-gl'

type MapStyleValue = Exclude<MapOptions['style'], undefined>
type MapStyleObject = Exclude<MapStyleValue, string>

export type BuiltInMapStyleId = 'routecamp' | 'openfreemap-liberty'
export type MapStyleId = BuiltInMapStyleId | 'custom'

export interface MapStylePreference {
  styleId: MapStyleId
  customStyleUrl?: string | null
  customStyleJson?: string | null
}

export const DEFAULT_MAP_STYLE_PREFERENCE: MapStylePreference = {
  styleId: 'routecamp',
}

const ROUTECAMP_STYLE_URL_OVERRIDE = import.meta.env.VITE_MAP_STYLE_URL?.trim()
const ROUTECAMP_STYLE_CACHE_BUSTER = __ROUTECAMP_MAP_STYLE_VERSION__.trim()
const ABSOLUTE_URL_RE = /^[a-z][a-z0-9+.-]*:/i

export const MAP_STYLE_OPTIONS: Array<{
  id: BuiltInMapStyleId
  label: string
  description: string
}> = [
  {
    id: 'routecamp',
    label: 'Routecamp',
    description: 'Routecamp map style',
  },
  {
    id: 'openfreemap-liberty',
    label: 'OpenFreeMap Liberty',
    description: 'OpenFreeMap vector style',
  },
]

function cloneMapStyle(style: unknown): MapStyleValue {
  return JSON.parse(JSON.stringify(style)) as MapStyleValue
}

function assertMapStyleObject(value: unknown, message: string): asserts value is MapStyleObject {
  if (!value || typeof value !== 'object') {
    throw new Error(message)
  }

  const styleObj = value as { version?: number }
  if (styleObj.version !== 8) {
    throw new Error(message)
  }
}

function resolveStyleDocumentUrl(styleUrl: string): string {
  if (ABSOLUTE_URL_RE.test(styleUrl)) {
    return styleUrl
  }

  const origin = typeof window === 'undefined' ? 'https://route.camp' : window.location.origin
  return `${origin}${styleUrl.startsWith('/') ? styleUrl : `/${styleUrl}`}`
}

function resolveStyleAssetUrl(assetUrl: string, styleUrl: string): string {
  if (ABSOLUTE_URL_RE.test(assetUrl)) {
    return assetUrl
  }

  const styleDocumentUrl = resolveStyleDocumentUrl(styleUrl)
  const styleOrigin = new URL(styleDocumentUrl).origin

  if (assetUrl.startsWith('//')) {
    return `${new URL(styleDocumentUrl).protocol}${assetUrl}`
  }

  if (assetUrl.startsWith('/')) {
    return `${styleOrigin}${assetUrl}`
  }

  const styleDirectoryUrl = styleDocumentUrl.slice(0, styleDocumentUrl.lastIndexOf('/') + 1)
  return `${styleDirectoryUrl}${assetUrl}`
}

export function absolutizeMapStyleAssetUrls(
  style: MapStyleObject,
  styleUrl = ROUTECAMP_STYLE_URL,
): MapStyleObject {
  const clonedStyle = cloneMapStyle(style) as MapStyleObject & {
    glyphs?: string
    sprite?: string | Array<{ id?: string; url?: string }>
  }

  if (typeof clonedStyle.sprite === 'string') {
    clonedStyle.sprite = resolveStyleAssetUrl(clonedStyle.sprite, styleUrl)
  } else if (Array.isArray(clonedStyle.sprite)) {
    clonedStyle.sprite = clonedStyle.sprite.map((sprite) => ({
      ...sprite,
      url: typeof sprite.url === 'string' ? resolveStyleAssetUrl(sprite.url, styleUrl) : sprite.url,
    }))
  }

  if (typeof clonedStyle.glyphs === 'string') {
    clonedStyle.glyphs = resolveStyleAssetUrl(clonedStyle.glyphs, styleUrl)
  }

  return clonedStyle
}

export async function loadRoutecampMapStyle(
  styleUrl = ROUTECAMP_STYLE_URL,
): Promise<MapStyleObject> {
  const response = await fetch(styleUrl)
  if (!response.ok) {
    throw new Error(`Failed to load Routecamp map style: HTTP ${response.status}`)
  }

  const styleJson: unknown = await response.json()
  assertMapStyleObject(styleJson, 'Routecamp style JSON must be a valid MapLibre style (version 8)')
  return absolutizeMapStyleAssetUrls(styleJson, styleUrl)
}

function hashStyleKey(input: string): string {
  let hash = 2166136261

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return (hash >>> 0).toString(16).padStart(8, '0')
}

export function normalizeMapStylePreference(
  value: MapStylePreference | null | undefined,
): MapStylePreference {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_MAP_STYLE_PREFERENCE }
  }

  if (value.styleId === 'custom') {
    const customStyleUrl = value.customStyleUrl?.trim()
    const customStyleJson = value.customStyleJson?.trim()

    if (customStyleJson) {
      return {
        styleId: 'custom',
        customStyleJson,
        customStyleUrl: customStyleUrl || null,
      }
    }

    if (customStyleUrl) {
      return {
        styleId: 'custom',
        customStyleUrl,
      }
    }

    return { ...DEFAULT_MAP_STYLE_PREFERENCE }
  }

  if (value.styleId === 'openfreemap-liberty' || value.styleId === 'routecamp') {
    return { styleId: value.styleId }
  }

  return { ...DEFAULT_MAP_STYLE_PREFERENCE }
}

export function resolveMapStyle(preference: MapStylePreference): MapStyleValue {
  if (preference.styleId === 'custom') {
    if (preference.customStyleJson) {
      return parseCustomMapStyleJson(preference.customStyleJson)
    }
    if (preference.customStyleUrl) {
      return preference.customStyleUrl
    }
  }

  if (preference.styleId === 'routecamp') {
    return ROUTECAMP_STYLE_URL
  }

  if (preference.styleId === 'openfreemap-liberty') {
    return OPENFREEMAP_LIBERTY_STYLE_URL
  }

  return ROUTECAMP_STYLE_URL
}

export async function resolveRuntimeMapStyle(
  preference: MapStylePreference,
): Promise<MapStyleValue> {
  const resolvedStyle = resolveMapStyle(preference)

  if (preference.styleId === 'routecamp' && typeof resolvedStyle === 'string') {
    return loadRoutecampMapStyle(resolvedStyle)
  }

  return resolvedStyle
}

export function getOfflineStyleCacheKey(preference: MapStylePreference): string {
  const normalized = normalizeMapStylePreference(preference)

  if (normalized.styleId !== 'custom') {
    return normalized.styleId
  }

  if (normalized.customStyleJson) {
    return `custom-json-${hashStyleKey(normalized.customStyleJson)}`
  }

  if (normalized.customStyleUrl) {
    return `custom-url-${hashStyleKey(normalized.customStyleUrl)}`
  }

  return DEFAULT_MAP_STYLE_PREFERENCE.styleId
}

export function mapStylePreferenceFromOfflineStyleCacheKey(
  styleCacheKey: string,
): MapStylePreference | null {
  if (styleCacheKey === 'routecamp' || styleCacheKey === 'openfreemap-liberty') {
    return { styleId: styleCacheKey }
  }

  return null
}

export function parseCustomMapStyleJson(raw: string): MapStyleValue {
  const parsed: unknown = JSON.parse(raw)

  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Custom style JSON must be an object')
  }

  assertMapStyleObject(parsed, 'Custom style JSON must be a valid MapLibre style (version 8)')

  return cloneMapStyle(parsed)
}

export const OPENFREEMAP_LIBERTY_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty'
export const ROUTECAMP_STYLE_URL = getRoutecampStyleUrl(
  ROUTECAMP_STYLE_URL_OVERRIDE,
  ROUTECAMP_STYLE_CACHE_BUSTER,
)
